import { Injectable, inject } from '@angular/core';
import { HttpBackend, HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { concatMap, map, switchMap } from 'rxjs/operators';
import { HttpService } from './http.service';
import { Customer, PortalUser, PagedResult, ApiResponse, AuthResponse, SimpleApiResponse, PaginatedUsersResponse, CustomerUpdate, ProductFeature, ProductLicense, UsersPagedResult, PagedApiResult, BroadcastMessage, EmailTemplate, Setting, AlertPolicy, LicenseHistoryResponse, PoolStatus, PoolEntry, PoolEntriesBulkDeleteResult, MspCompany, MspCompanyStatus, CreateUserRequest } from '../models/types';
import { environment } from '@environments/environment';
import { LoggerService } from './logger.service';
import { TrialSignupPayload } from '@models/onboarding.types';

// API Constants
const API_BASE = environment?.api_url ?? '';

/**
 * Fixed Content-Type for agent MSI uploads.
 * Browsers disagree on .msi MIME (application/x-msdownload, application/x-msi, empty, etc.).
 * Presigned S3 URLs require an exact Content-Type match — never use File.type.
 */
export const AGENT_INSTALLER_CONTENT_TYPE = 'application/octet-stream';

export interface AgentUploadPresignData {
  uploadId: string;
  uploadUrl: string;
}

export interface PoolUploadSession {
  uploadId: string;
  uploadUrl: string;
  customerId: string;
  fileName: string;
  fileSize: string;
  fileType: string;
  productVersion: string;
}

const ENDPOINTS = {
  // Auth
  login: `${API_BASE}/auth/login`,
  logout: `${API_BASE}/auth/logout`,
  verifyEmail: `${API_BASE}/auth/verify-email`,
  forgotPassword: `${API_BASE}/auth/forgot-password`,
  resetPasswordByEmail: `${API_BASE}/users/reset-pass-by-email`,
  resetPassword: `${API_BASE}/auth/reset-password`,

  // Customers
  customers: `${API_BASE}/customer`,
  signUpTrial: `${API_BASE}/customer/signup-trial`,
  customer: (id: string) => `${API_BASE}/customer/${id}`,
  customerStatus: (id: string) => `${API_BASE}/customer/${id}/status`,
  checkSubdomain: (subdomain: string) => `${API_BASE}/customer/check-domain/${subdomain}`,
  checkEmail: (email: string) => `${API_BASE}/customer/check-email?email=${encodeURIComponent(email)}`,
  tenantBySubdomain: (subdomain: string) => `${API_BASE}/customer/subdomain/${subdomain}`,
  customerFeatures: (id: string) => `${API_BASE}/customer/${id}/features`,
  customerLicenses: (id: string) => `${API_BASE}/customer/${id}/licenses`,
  customerDataRetention: (id: string) => `${API_BASE}/customer/${id}/data-retention`,
  customerLicenseHistory: `${API_BASE}/customer/license-history`,
  customerWelcomeEmail: `${API_BASE}/customer/welcome-email`,
  customerTriggerUpdate: `${API_BASE}/customer/trigger-update`,
  customerRefreshCache: `${API_BASE}/customer/refresh-cache`,

  // Admin Users
  firstTimePasswordReset: `${API_BASE}/users/first-time`,
  validatePasswordLink: `${API_BASE}/users/validate-password-link`,
  currentUser: `${API_BASE}/users/me`,
  users: `${API_BASE}/users`,

  // MSP Companies
  mspCompanies: `${API_BASE}/msp-companies`,
  mspCompany: (id: string) => `${API_BASE}/msp-companies/${id}`,
  mspCompanyStatus: (id: string) => `${API_BASE}/msp-companies/${id}/status`,
  user: (id: string) => `${API_BASE}/users/${id}`,
  deleteUser: (id: string) => `${API_BASE}/users/${id}`,
  addUser: `${API_BASE}/users`,
  updateUser: (id: string) => `${API_BASE}/users/${id}`,
  updateUserStatus: (id: string) => `${API_BASE}/users/${id}/status`,
  updateUserMfaStatus: (id: string) => `${API_BASE}/users/${id}/mfa-status`,
  resetUserPassword: `${API_BASE}/users/reset-pass`,
  customerUsers: (id: string) => `${API_BASE}/users/customer/${id}`,  //Customer's Users
  customerActiveUsers: (id: string) => `${API_BASE}/users/customer/${id}/active`,  //Customer's Active Users

  //Broadcast
  sendBroadcast: `${API_BASE}/broadcast`,
  getBroadcastById: (id: string) => `${API_BASE}/broadcast/${id}`,
  getBroadcasts: `${API_BASE}/broadcasts`,

  // Logs
  userLogs: (userId: string) => `${API_BASE}/activity/user/${userId}`,

  // Onboarding
  onboarding: `${API_BASE}/onboarding`,
  sendOtp: `${API_BASE}/users/send-code`,
  verifyOtp: `${API_BASE}/users/verify-code`,

  //utilities
  templates: `${API_BASE}/utility/templates`,
  template: (id: string) => `${API_BASE}/utility/templates/${id}`,
  updateTemplate: `${API_BASE}/utility/templates`,

  settings: `${API_BASE}/utility/settings`,
  //setting: (id: string) => `${API_BASE}/utility/settings/${id}`,
  updateSetting: `${API_BASE}/utility/settings`,

  alert_policies: `${API_BASE}/utility/alert_policies`,
  //alert_policy: (id: string) => `${API_BASE}/utility/alert_policies/${id}`,
  updateAlertPolicy: `${API_BASE}/utility/alert_policies`,

  // Agent Upload (presign → S3 PUT → confirm)
  agentUploadPresign: `${API_BASE}/utility/agent-upload`,
  agentUploadConfirm: `${API_BASE}/utility/agent-upload/confirm`,

  // Agent Pool (presign → S3 PUT → confirm)
  poolUploadPresign: `${API_BASE}/customer/pool/upload-agent`,
  poolUploadConfirm: `${API_BASE}/customer/pool/upload-agent/confirm`,
  poolUploadCancel: `${API_BASE}/customer/pool/upload-agent/cancel`,
  poolStatus: `${API_BASE}/customer/pool/status`,
  poolEntries: `${API_BASE}/customer/pool/entries`,
  poolEntry: (customerId: string) => `${API_BASE}/customer/pool/entries/${customerId}`,
  poolEntriesDelete: `${API_BASE}/customer/pool/entries/delete`,

  // Admin SQL
  runSql: `${API_BASE}/utility/admin/run_sql`,

};

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private http = inject(HttpService);
  private logger = inject(LoggerService);
  /** Bypasses interceptors so nothing can alter S3 Content-Type / Authorization. */
  private readonly s3Http = new HttpClient(inject(HttpBackend));

  /**
   * Upload agent installer via presigned S3 PUT (direct-to-S3), then confirm metadata.
   * Content-Type is always application/octet-stream — browsers often mis-detect .msi MIME types.
   */
  uploadAgentFile(data: {
    customerId: string;
    file: File;
    uploadDate: string;
    reason: string;
    productVersion: string;
  }): Observable<HttpEvent<unknown>> {
    const request = {
      customerId: data.customerId,
      fileName: data.file.name,
      fileSize: data.file.size.toString(),
      fileType: AGENT_INSTALLER_CONTENT_TYPE,
      uploadDate: data.uploadDate,
      reason: data.reason,
      productVersion: data.productVersion,
    };

    return this.getAgentUploadUrl(request).pipe(
      switchMap((response) => {
        if (!response?.success || !response?.data?.uploadId || !response.data.uploadUrl) {
          return throwError(
            () => new Error(response?.message || 'Upload URL was not returned by the server.')
          );
        }

        const { uploadId, uploadUrl } = response.data;
        const confirmRequest = { ...request, uploadId };

        // Ignore browser File.type entirely — re-type without copying bytes.
        const body = data.file.slice(0, data.file.size, AGENT_INSTALLER_CONTENT_TYPE);

        // HttpHeaders + HttpRequest keeps Content-Type exact (no charset, no File.type fallback).
        const headers = new HttpHeaders({
          'Content-Type': AGENT_INSTALLER_CONTENT_TYPE,
        });

        const s3Put = new HttpRequest('PUT', uploadUrl, body, {
          headers,
          reportProgress: true,
          responseType: 'text',
        });

        return this.s3Http.request(s3Put).pipe(
          concatMap((event) => {
            if (event.type === HttpEventType.Response) {
              return this.confirmAgentUpload(confirmRequest).pipe(
                map((confirmResponse) => {
                  if (confirmResponse && confirmResponse.success === false) {
                    throw new Error(
                      confirmResponse.message || 'Upload confirmation failed.'
                    );
                  }
                  return event;
                })
              );
            }
            return of(event);
          })
        );
      })
    );
  }

  /**
   * Upload pooled agent installer via presigned S3 PUT, then confirm pool entry.
   * Filename must be {guid}.msi; Content-Type is always application/octet-stream.
   */
  uploadPoolAgent(file: File, productVersion: string): Observable<HttpEvent<unknown>> {
    return this.createPoolUploadSession(file, productVersion).pipe(
      switchMap((session) => this.putPoolAgentFile(session, file).pipe(
        concatMap((event) => event.type === HttpEventType.Response
          ? this.confirmPoolUpload(session).pipe(map((response) => {
              if (!response.success) {
                throw new Error(response.message || 'Pool upload confirmation failed.');
              }
              return event;
            }))
          : of(event))
      ))
    );
  }

  createPoolUploadSession(
    file: File,
    productVersion: string,
    uploadId = ''
  ): Observable<PoolUploadSession> {
    const request = {
      uploadId,
      customerId: file.name.replace(/\.msi$/i, ''),
      fileName: file.name,
      fileSize: file.size.toString(),
      fileType: AGENT_INSTALLER_CONTENT_TYPE,
      productVersion,
    };

    return this.getPoolUploadUrl(request).pipe(
      map((response) => {
        if (!response?.success || !response?.data?.uploadId || !response.data.uploadUrl) {
          throw new Error(response?.message || 'Upload URL was not returned by the server.');
        }
        return { ...request, ...response.data };
      })
    );
  }

  putPoolAgentFile(session: PoolUploadSession, file: File): Observable<HttpEvent<unknown>> {
    const body = file.slice(0, file.size, AGENT_INSTALLER_CONTENT_TYPE);
    const headers = new HttpHeaders({ 'Content-Type': AGENT_INSTALLER_CONTENT_TYPE });
    return this.s3Http.request(new HttpRequest('PUT', session.uploadUrl, body, {
      headers,
      reportProgress: true,
      responseType: 'text',
    }));
  }

  getPoolUploadUrl(data: {
    customerId: string;
    fileName: string;
    fileSize: string;
    fileType: string;
    productVersion: string;
  }): Observable<ApiResponse<AgentUploadPresignData>> {
    return this.http.post<ApiResponse<AgentUploadPresignData>>(ENDPOINTS.poolUploadPresign, data);
  }

  confirmPoolUpload(data: {
    uploadId: string;
    customerId: string;
    fileName: string;
    fileSize: string;
    fileType: string;
    productVersion: string;
  }): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(ENDPOINTS.poolUploadConfirm, data);
  }

  cancelPoolUpload(data: PoolUploadSession): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(ENDPOINTS.poolUploadCancel, data);
  }

  getPoolStatus(): Observable<ApiResponse<PoolStatus>> {
    return this.http.get<ApiResponse<PoolStatus>>(ENDPOINTS.poolStatus);
  }

  getPoolEntries(limit = 10): Observable<ApiResponse<PoolEntry[]>> {
    return this.http.get<ApiResponse<PoolEntry[]>>(ENDPOINTS.poolEntries, { limit });
  }

  deletePoolEntry(customerId: string): Observable<ApiResponse<unknown>> {
    return this.http.delete<ApiResponse<unknown>>(ENDPOINTS.poolEntry(customerId));
  }

  deletePoolEntries(customerIds: string[]): Observable<ApiResponse<PoolEntriesBulkDeleteResult>> {
    return this.http.post<ApiResponse<PoolEntriesBulkDeleteResult>>(ENDPOINTS.poolEntriesDelete, {
      customerIds,
    });
  }

  // Auth Methods
  login(credentials: { username: string; password: string }): Observable<{ token: string; user: any }> {
    this.logger.debug('Logging in with credentials:', credentials);
    return this.http.post<{ token: string; user: any }>(ENDPOINTS.login, credentials);
  }

  logout(): Observable<any> {
    return this.http.post(ENDPOINTS.logout, {});
  }

  verifyEmail(token: string): Observable<any> {
    return this.http.post(ENDPOINTS.verifyEmail, { token });
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(ENDPOINTS.forgotPassword, { email });
  }

  resetPasswordByEmail(userEmail: string): Observable<any> {
    return this.http.post(ENDPOINTS.resetPasswordByEmail, { email: userEmail });
  }

  resetPassword(data: { token: string; password: string; confirmPassword: string }): Observable<any> {
    return this.http.post(ENDPOINTS.resetPassword, data);
  }

  /**
   * First-time password reset via invitation link.
   * @param data.token   The invitation token from the URL
   * @param data.password The new password encoded in Base64
   */
  firstTimePasswordReset(data: { token: string; password: string }): Observable<ApiResponse<{ username: string }>> {
    return this.http.post<ApiResponse<{ username: string }>>(
      ENDPOINTS.firstTimePasswordReset,
      data
    );
  }

  /**
   * Validates a password setup/reset link token without setting a password.
   */
  validatePasswordLink(token: string, flow?: string): Observable<SimpleApiResponse> {
    return this.http.post<SimpleApiResponse>(ENDPOINTS.validatePasswordLink, { token, flow });
  }

  getCurrentUser(): Observable<any> {
    return this.http.get(ENDPOINTS.currentUser);
  }

  sendOtp(email: string): Observable<SimpleApiResponse> {
    return this.http.post(ENDPOINTS.sendOtp, { email });
  }

  verifyOtp(email: string, code: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(ENDPOINTS.verifyOtp, { email, code });
  }

  // Customer Methods
  signUpTrial(customer: TrialSignupPayload): Observable<ApiResponse<Customer>> {
    return this.http.post<ApiResponse<Customer>>(ENDPOINTS.signUpTrial, customer);
  }

  sendBroadcast(data: BroadcastMessage): Observable<ApiResponse<BroadcastMessage>> {
    var tt = this.http.post<ApiResponse<BroadcastMessage>>(ENDPOINTS.sendBroadcast, data);
    return tt;
  }

  getCustomers(pageToken: string, limit?: number, query?: string, status?: string): Observable<ApiResponse<PagedApiResult<Customer>>> {
    var tt = this.http.get<ApiResponse<PagedApiResult<Customer>>>(ENDPOINTS.customers, { token: pageToken, limit, query, status });
    return tt;
  }

  getCustomer(id: string): Observable<Customer> {
    return this.http.get<Customer>(ENDPOINTS.customer(id));
  }

  createCustomer(customer: Partial<Customer>): Observable<Customer> {
    return this.http.post<Customer>(ENDPOINTS.customers, customer);
  }

  updateCustomer(customer: CustomerUpdate): Observable<Customer> {
    return this.http.put<Customer>(ENDPOINTS.customer(customer.customerId), customer);
  }

  deleteCustomer(id: string): Observable<any> {
    return this.http.delete(ENDPOINTS.customer(id));
  }

  updateCustomerStatus(id: string, status: string): Observable<any> {
    return this.http.patch(ENDPOINTS.customerStatus(id), { customerId: id, status });
  }

  updateCustomerFeatures(id: string, features: ProductFeature[]): Observable<any> {
    const body = features.map((f) => ({
      featureId: f.featureId,
      featureCode: f.featureCode,
      isEnabled: f.isEnabled,
      code: f.featureCode,
      enabled: f.isEnabled,
    }));
    return this.http.post(ENDPOINTS.customerFeatures(id), body);
  }

  updateCustomerLicenses(id: string, licenses: ProductLicense[]): Observable<any> {
    return this.http.post(ENDPOINTS.customerLicenses(id), licenses);
  }

  updateCustomerDataRetention(id: string, dataRetention: { enabled: boolean; period: string }): Observable<any> {
    return this.http.post(ENDPOINTS.customerDataRetention(id), dataRetention);
  }

  getLicenseHistory(customerId: string, limit: number = 0): Observable<LicenseHistoryResponse> {
    return this.http.get<LicenseHistoryResponse>(ENDPOINTS.customerLicenseHistory, { id: customerId, limit });
  }

  resendCustomerWelcomeEmail(customerId: string): Observable<SimpleApiResponse> {
    return this.http.post<SimpleApiResponse>(ENDPOINTS.customerWelcomeEmail, { customerId });
  }

  triggerCustomerUpdate(
    customerId: string,
    action: 'BUILD_AGENT' | 'LICENSE_UPDATE' | 'LICENSE_USAGE_UPDATE'
  ): Observable<SimpleApiResponse> {
    return this.http.post<SimpleApiResponse>(ENDPOINTS.customerTriggerUpdate, { customerId, action });
  }
  refreshCustomerCache(customerId: string): Observable<SimpleApiResponse> {
    return this.http.post<SimpleApiResponse>(`${ENDPOINTS.customerRefreshCache}?customerId=${encodeURIComponent(customerId)}`, {});
  }

  checkSubdomainAvailability(subdomain: string): Observable<{ isAvailable: boolean, message: string }> {
    return this.http.get<{ isAvailable: boolean, message: string }>(ENDPOINTS.checkSubdomain(subdomain));
  }

  checkEmailAvailability(email: string): Observable<{ isAvailable: boolean, message: string }> {
    return this.http.get<{ isAvailable: boolean, message: string }>(ENDPOINTS.checkEmail(email));
  }

  getTenantBySubdomain(subdomain: string): Observable<any> {
    return this.http.get<any>(ENDPOINTS.tenantBySubdomain(subdomain));
  }

  // MSP Company Methods
  getMspCompanies(
    options: { limit?: number; token?: string; query?: string; status?: MspCompanyStatus | '' } = {}
  ): Observable<ApiResponse<PagedApiResult<MspCompany> | null>> {
    // Backend expects all four parameters on every request, sent as empty strings when unused
    const params = {
      limit: options.limit ?? 20,
      token: options.token ?? '',
      query: options.query ?? '',
      status: options.status ?? '',
    };
    return this.http.get<ApiResponse<PagedApiResult<MspCompany> | null>>(ENDPOINTS.mspCompanies, params);
  }

  createMspCompany(company: { name: string; email: string }): Observable<ApiResponse<MspCompany | null>> {
    return this.http.post<ApiResponse<MspCompany | null>>(ENDPOINTS.mspCompanies, company);
  }

  updateMspCompany(id: string, company: { name: string; email: string }): Observable<ApiResponse<MspCompany | null>> {
    return this.http.put<ApiResponse<MspCompany | null>>(ENDPOINTS.mspCompany(id), { mspCompanyId: id, ...company });
  }

  updateMspCompanyStatus(id: string, status: MspCompanyStatus): Observable<ApiResponse<MspCompany | null>> {
    return this.http.patch<ApiResponse<MspCompany | null>>(ENDPOINTS.mspCompanyStatus(id), { mspCompanyId: id, status });
  }

  // Admin User Methods
  getUsers(pageSize: number = 10, token?: string, query?: string, role?: string): Observable<ApiResponse<PagedApiResult<PortalUser>>> {
    const params: any = { limit: pageSize };
    if (token) params.token = token;
    if (query) params.query = query;
    if (role) params.role = role;
    return this.http.get<ApiResponse<PagedApiResult<PortalUser>>>(ENDPOINTS.users, params);
  }

  getUser(id: string): Observable<ApiResponse<PortalUser>> {
    return this.http.get<ApiResponse<PortalUser>>(ENDPOINTS.user(id));
  }

  createUser(user: CreateUserRequest): Observable<ApiResponse<PortalUser>> {
    return this.http.post<ApiResponse<PortalUser>>(ENDPOINTS.users, user);
  }

  updateUser(id: string, user: Partial<PortalUser>): Observable<ApiResponse<PortalUser>> {
    return this.http.put<ApiResponse<PortalUser>>(ENDPOINTS.user(id), user);
  }

  updateUserStatus(id: string, isActive: boolean): Observable<ApiResponse<PortalUser>> {
    return this.http.patch<ApiResponse<PortalUser>>(ENDPOINTS.updateUserStatus(id), { UserId: id, isActive });
  }

  updateUserMfaStatus(id: string, isEnabled: boolean): Observable<ApiResponse<string>> {
    return this.http.patch<ApiResponse<string>>(ENDPOINTS.updateUserMfaStatus(id), { UserId: id, isActive: isEnabled });
  }

  resetUserPassword(userId: string): Observable<SimpleApiResponse> {
    return this.http.post<SimpleApiResponse>(ENDPOINTS.resetUserPassword, { userId });
  }

  deleteUser(id: string): Observable<SimpleApiResponse> {
    return this.http.delete<SimpleApiResponse>(ENDPOINTS.deleteUser(id));
  }

  // Logs Methods
  getUserLogs(userId: string, page?: number, pageSize?: number): Observable<any[]> {
    return this.http.get<any[]>(ENDPOINTS.userLogs(userId), { page, pageSize });
  }

  // Onboarding Methods
  submitOnboarding(data: any): Observable<any> {
    return this.http.post(ENDPOINTS.onboarding, data);
  }

  // Utility
  getTemplates(): Observable<EmailTemplate[]> {
    return this.http.get<EmailTemplate[]>(ENDPOINTS.templates);
  }

  getTemplate(id: string): Observable<EmailTemplate> {
    return this.http.get<EmailTemplate>(ENDPOINTS.template(id));
  }

  saveTemplate(template: EmailTemplate): Observable<EmailTemplate> {
    return this.http.post<EmailTemplate>(ENDPOINTS.updateTemplate, template);
  }

  getSettings(): Observable<Setting[]> {
    return this.http.get<Setting[]>(ENDPOINTS.settings);
  }

  saveSettings(template: Setting): Observable<Setting> {
    return this.http.post<Setting>(ENDPOINTS.settings, template);
  }

  getAlertPolicy(): Observable<AlertPolicy[]> {
    return this.http.get<AlertPolicy[]>(ENDPOINTS.alert_policies);
  }

  saveAlertPolicy(template: AlertPolicy): Observable<AlertPolicy> {
    return this.http.post<AlertPolicy>(ENDPOINTS.updateAlertPolicy, template);
  }

  // Agent Upload
  getAgentUploadUrl(data: {
    customerId: string;
    fileName: string;
    fileSize: string;
    fileType: string;
    uploadDate: string;
    reason: string;
    productVersion: string;
  }): Observable<ApiResponse<AgentUploadPresignData>> {
    return this.http.post<ApiResponse<AgentUploadPresignData>>(
      ENDPOINTS.agentUploadPresign,
      data
    );
  }

  confirmAgentUpload(data: {
    uploadId: string;
    customerId: string;
    fileName: string;
    fileSize: string;
    fileType: string;
    uploadDate: string;
    reason: string;
    productVersion: string;
  }): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(
      ENDPOINTS.agentUploadConfirm,
      data
    );
  }

  // Admin SQL Query
  runSql(encodedQuery: string): Observable<any> {
    return this.http.post<any>(ENDPOINTS.runSql, { query: encodedQuery });
  }

}
